import { useEffect } from 'react';
import { camelCaseKeys, ObjectType } from 'utils/HelperFunctions/camelCaseKeys/camelCaseKeys';
import getDeviceType from 'utils/HelperFunctions/getDeviceType';
import getLandingPageUrl from 'utils/HelperFunctions/getLandingPageUrl';

/**
 * Custom hook to capture and store tracking parameters from the URL into local storage.
 * This hook extracts UTM parameters, device type, and other relevant tracking information
 * from the URL's query string and saves them in local storage under the key 'trackingParams'.
 *
 *  gets all of the URL search params and adds them to local storage
 *  used so that whenever a new account is created, it can credit the source
 *  in the marketing_source table (i.e. for paid ad campaigns, etc)
 *
 *  example URL: https://www.topuptest.com/campaign/ab-testing-tool?utm_source=
 *  facebook&utm_medium=cpc&utm_campaign=first_ad_campaign&utm_content=ad1&utm_term=
 *  ab%20testing%20tool&is_paid=true&ad_set=mobiles_keyword_history&coupon=FREE1000
 *
 *  have these params:
 *  - utm_source (e.g. facebook)
 *  - utm_medium (e.g. cpc)
 *  - utm_campaign (e.g. summer_sale_2024)
 *  - utm_content (e.g. ad1)
 *  - utm_term (e.g. ab%20testing%20tool)
 *  - is_paid (e.g. true)
 *  - ad_set (optional - e.g. mobiles_keyword_history)
 *  - coupon=SOMECOUPON123
 */
const useTrackingParams = () => {
    useEffect(() => {
        const getParams = () => {
            // check for existing params and merge with URL params (so we don't keep an incomplete object)
            const existingRaw = localStorage.getItem('trackingParams');
            const existing = existingRaw ? JSON.parse(existingRaw) : {};

            const searchParams = new URLSearchParams(window.location.search);
            let params: ObjectType = { ...existing };

            // Add/merge UTM/URL params (URL wins for new values)
            searchParams.forEach((value, key) => {
                params[key] = value;
            });

            // Ensure landing page URL and device exist (required by server)
            params['landingPageUrl'] = params['landingPageUrl'] || getLandingPageUrl();
            params['device'] = params['device'] || getDeviceType();

            // camel case keys
            params = camelCaseKeys(params) as ObjectType;

            // handle isPaid
            params['isPaid'] = params.isPaid === 'true';

            // set in local storage
            localStorage.setItem('trackingParams', JSON.stringify(params));
        };

        getParams();
    }, []);
};

export default useTrackingParams;
